<template>
  <div>
    <tool-bar page-title="Port.AR">
      <!-- Header -->
      <template>
        <div class="main-header">
          <h3 class="title" v-if="selectedPortfolio">
            {{ $t('portAr.headerSummary', { name: selectedPortfolio.name.split('.')[0], count: positionsCount }) }}
          </h3>
          <h3 class="title" v-else>
            {{ $t('portAr.headerSummaryEmpty') }}
          </h3>
        </div>
      </template>

      <template v-slot:settingsMocked>
        <v-card class="d-flex flex-row align-center justify-space-between pa-3 mt-3" outlined>
          <!-- SWITCH -->
          <v-switch
            v-model="mockFeedEnabled"
            color="#ffbf00"
            hide-details
            inset
            density="compact"
            class="ma-0 pa-0"
          ></v-switch>

          <!-- LABEL -->
          <span 
            class="white--text text-capitalize ml-3"
            :style="{ fontWeight: isMockDataOn ? 'bold' : 'normal' }"
          >
            {{ $t('dVolt.mockFeed') }}
          </span>
        </v-card>
      </template>
    </tool-bar>

    <div v-if="!handleBlur"  class="portfolio-app">
      <new-port-modal @new-portfolio="handleImport" v-model="showNewModal" />
      <import-port-modal @table-items="handleImport" v-model="showImportModal" />

      <v-main>
        <div class="portar-layout">
          <!-- Left sidebar: portfolio list -->
          <side-bar-menu width="300px" side="left">
            <!-- Slot `nav`: o corpo da sidebar some ao recolher, este nao. O
                 seletor de produto e o que decide a tela inteira, entao precisa
                 sobreviver ao recolhimento (vira so o icone). -->
            <template v-slot:nav="{ expanded }">
              <div class="product-switch" :class="{ 'is-collapsed': !expanded }">
                <div v-if="expanded" class="product-label">{{ $t('portAr.product.label') }}</div>
                <v-btn-toggle
                  :value="productType"
                  @change="onProductChange"
                  mandatory
                  dense
                  :class="expanded ? 'product-toggle' : 'product-toggle product-toggle--icons'"
                >
                  <v-btn
                    v-for="opt in productOptions"
                    :key="opt.value"
                    :value="opt.value"
                    small
                    text
                    class="product-btn"
                  >
                    <v-icon small :class="expanded ? 'mr-1' : ''">{{ opt.icon }}</v-icon>
                    <span v-if="expanded">{{ opt.label }}</span>
                  </v-btn>
                </v-btn-toggle>
              </div>
            </template>

            <div class="sidebar-section">
              <v-text-field
                v-model="portfolioQuery"
                :placeholder="$t('dVolt.searchPortfolios')"
                dense outlined solo flat rounded
                background-color="transparent"
                class="search-input"
                prepend-inner-icon="mdi-magnify"
              />
              <div class="sidebar-actions">
                <base-button isActive @click="openModal('new')">
                  <v-icon class="mr-2">mdi-plus</v-icon>
                  {{ $t('instructions.new') }}
                </base-button>
                <base-button @click="openModal('import')">
                  <v-icon class="mr-2">mdi-file-import-outline</v-icon>
                  {{ $t('instructions.import') }}
                </base-button>
              </div>
            </div>
            <v-divider class="divider" />
            <v-list dense>
              <v-list-item
                v-for="p in searchQuery"
                :key="p.id"
                class="portfolio-item"
                :class="selectedPortfolio && selectedPortfolio.id === p.id ? 'is-active' : ''"
                @click="onSelectPortfolio(p)"
              >
                <v-list-item-content>
                  <div class="d-flex justify-space-between">
                    <div class="d-flex flex-column align-start">
                      <div class="d-flex">
                        <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                        <v-list-item-title class="portfolio-name">{{ p.name }}</v-list-item-title>
                      </div>
                      <div>
                        <v-list-item-subtitle class="portfolio-sub mt-2">
                          {{ isNdf
                            ? $t('portAr.product.contractsSummary', { count: countPositions(p), currency: p.currency })
                            : $t('dVolt.positionsSummary', { count: countPositions(p), currency: p.currency }) }}
                        </v-list-item-subtitle>
                      </div>
                    </div>
                    <div class="d-flex">
                      <v-btn @click="handleDownload(p)" :disabled="countPositions(p) === 0" icon>
                        <v-icon small>mdi-download</v-icon>
                      </v-btn>
                      <v-btn @click="handleDeletePortfolio(p.id)" icon>
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-list-item-content>
              </v-list-item>
              <div v-if="!searchQuery.length" class="empty-list">
                {{ $t('portAr.product.emptyForProduct') }}
              </div>
            </v-list>
          </side-bar-menu>

          <!-- Main content -->
          <div class="portar-content">
            <div class="tabs-wrap">
              <v-tabs background-color="transparent" height="44" slider-size="2" class="tabs" color="#ffbf00">
                <v-tab v-for="(item, index) in menuItems" :key="index" :disabled="item.disabled" :to="item.link">
                  <v-icon v-if="item.icon" class="mr-2">{{ item.icon }}</v-icon>
                  {{ item.title }}
                </v-tab>
              </v-tabs>
            </div>
            <router-view :mockedEnabled="mockFeedEnabled" />
          </div>

          <!-- Right sidebar: portfolio properties -->
          <side-bar-menu width="320px" side="right" :initial-expanded="true">
            <div class="props-header">
              <div class="props-title mb-1">{{ $t('dVolt.portfolioProperties') }}</div>
              <div class="props-date">{{ formattedPortfolioDate }}</div>
            </div>
            <v-divider />
            <div v-if="selectedPortfolio" class="props-cards">
              <v-card flat class="card box">
                <div class="box-label">{{ $t('dVolt.portfolioName') }}</div>
                <div class="box-value text-no-wrap">{{ selectedPortfolio.name }}</div>
              </v-card>
              <v-card flat class="card box">
                <div class="box-label">{{ $t('dVolt.baseCurrency') }}</div>
                <div class="box-value">{{ selectedPortfolio.currency }}</div>
              </v-card>

              <!-- Um NDF nao tem "valor de mercado": na origem ele vale zero. O
                   que se acompanha e notional (exposicao) e MTM (marcacao). -->
              <template v-if="isNdf">
                <v-card flat class="card box">
                  <div class="box-label">{{ $t('ndfPositions.netNotional') }}</div>
                  <div class="box-value" :class="ndfSummary.netNotionalBRL < 0 ? 'danger' : 'positive'">
                    {{ compactMoney(ndfSummary.netNotionalBRL, true) }}
                  </div>
                  <div class="box-hint">
                    {{ $t('ndfPositions.grossNotional', { value: compactMoney(ndfSummary.grossNotionalBRL, false) }) }}
                  </div>
                </v-card>
                <v-card flat class="card box">
                  <div class="box-label">{{ $t('ndfPositions.mtmTotal') }}</div>
                  <div class="box-value" :class="ndfSummary.mtm < 0 ? 'danger' : 'positive'">
                    {{ compactMoney(ndfSummary.mtm, true) }}
                  </div>
                  <div class="box-hint">{{ $t('ndfPositions.mtmNominalNote') }}</div>
                </v-card>
                <v-card flat class="card box">
                  <div class="box-label">
                    <v-icon medium>mdi-calendar-range</v-icon>
                    {{ $t('ndfPositions.props.wam') }}
                  </div>
                  <div class="box-value">{{ $t('ndfPositions.props.days', { count: Math.round(wam) }) }}</div>
                  <div class="box-hint">
                    {{ $t('ndfPositions.props.nextMaturity', { date: formattedNextMaturity }) }}
                  </div>
                </v-card>
                <v-card flat class="card box">
                  <div class="box-label">
                    <v-icon medium>mdi-bank-outline</v-icon>
                    {{ $t('ndfPositions.props.counterparties') }}
                  </div>
                  <div class="box-value">{{ counterpartiesCount }}</div>
                </v-card>
              </template>

              <template v-else>
                <v-card flat class="card box">
                  <div class="box-label">{{ $t('dVolt.totalValue') }}</div>
                  <div class="box-value">{{ formattedValue }}</div>
                </v-card>
                <v-card flat class="card box">
                  <div class="box-label">{{ $t('dVolt.unrealizedPnl') }}</div>
                  <div class="box-value" :class="portfolioStore.unrealizedPnL().split(/(?=[A-Za-z])/)[0] < 0 ? 'danger' : 'positive'">
                    {{ selectedPortfolio.currency }} {{ unrealizedPnl }}
                  </div>
                  <div class="box-hint">{{ $t('dVolt.percentOfTotalValue', { percent: hintValue.toFixed(2) }) }}</div>
                </v-card>
              </template>

              <v-card flat class="card box">
                <div class="box-label">
                  <v-icon medium>mdi-tag-outline</v-icon>
                  {{ isNdf ? $t('ndfPositions.props.contracts') : $t('dVolt.positions') }}
                </div>
                <div class="box-value">{{ countPositions(selectedPortfolio) }}</div>
              </v-card>
              <v-card flat class="card box d-flex flex-column align-start">
                <div class="box-label">{{ $t('quoteInfo.status') }}</div>
                <v-chip small class="strong" :class="portfolioStore.getStatus ? 'chip-status-active' : 'chip-status-desactive'">
                  {{ portfolioStore.getStatus ? $t('broker.status.active') : $t('broker.status.paused') }}
                </v-chip>
              </v-card>
              <v-card flat class="card box">
                <div class="box-label">
                  <v-icon medium>mdi-calendar-blank</v-icon>
                  {{ $t('dVolt.lastUpdated') }}
                </div>
                <div class="box-hint">{{ formattedLastUpdated }}</div>
              </v-card>
            </div>
          </side-bar-menu>
        </div>
      </v-main>
    </div>
    <div v-else >
      <img src="../assets/blur_imgs/blur1.png" :alt="$t('portAr.blurredImageAlt')">
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { usePortfolioStore } from "@/store/portfolioStore.js";
import ToolBar from "../components/layout/ToolBar.vue";
import ImportPortModal from "@/components/modal/ImportPortModal.vue";
import NewPortModal from "@/components/modal/NewPortModal.vue";
import { useCoreStore } from "@/store/coreStore";
import SideBarMenu from "@/components/layout/SideBarMenu.vue";
import { weightedAverageMaturity } from "@/utils/ndfPortfolio";

export default {
  name: "Port.AR",
  components: { ToolBar, ImportPortModal, NewPortModal, SideBarMenu },
  data() {
    return {
      mockFeedEnabled: true,
      showImportModal: false,
      showNewModal: false,
      showAlertModal: false,
      portfolioQuery: "",
      positionQuery: "",
      sortKey: "",
      sortAsc: true,
    }
  },
  mounted() {
    // Seleção inicial no store (caso ainda não exista)
    if ((this.portfolios?.length || 0) > 0 && !this.selectedPortfolio) {
      this.portfolioStore.selectPortfolio(this.portfolios[0].id);
    }
  },
  computed: {
    ...mapStores(usePortfolioStore),
    ...mapStores(useCoreStore),
    menuItems() {
      return [
        // "mdi-chart" nao existe no Material Design Icons - nome invalido nao
        // avisa, so nao desenha, e as duas abas ficavam sem icone.
        { title: this.$t('dVolt.positions'), icon: "mdi-table", link: "/portar/positions" },
        {
          title: this.$t('dVolt.analytics'),
          icon: "mdi-chart-line",
          link: "/portar/analytics",
        },
      ];
    },
    handleBlur()
    {
      return this.coreStore.evaluateBlur(this.$options.name)
    },
    productType() {
      return this.portfolioStore.productType;
    },
    isNdf() {
      return this.portfolioStore.isNdf;
    },
    productOptions() {
      return [
        { value: 'ETF', label: this.$t('portAr.product.etf'), icon: 'mdi-chart-donut' },
        { value: 'NDF', label: this.$t('portAr.product.ndf'), icon: 'mdi-swap-horizontal-bold' },
      ];
    },
    ndfSummary() {
      return this.portfolioStore.ndfSummary;
    },
    wam() {
      return weightedAverageMaturity(this.portfolioStore.ndfRows);
    },
    counterpartiesCount() {
      return new Set(
        this.portfolioStore.ndfRows.map((r) => r.counterparty).filter(Boolean)
      ).size;
    },
    formattedNextMaturity() {
      const iso = this.ndfSummary.nextMaturity;
      if (!iso) return '-';
      const [y, m, d] = String(iso).slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    },
    portfolios() {
      return this.portfolioStore?.getPortfolios || [];
    },
    selectedPortfolio() {
      return this.portfolioStore?.selectedPortfolio || null;
    },
    positionsSnapshot() {
      return this.portfolioStore?.getPositions || [];
    },
    positionsCount() {
      return (this.portfolioStore.getPositions || []).length;
    },
    totalValue() {
      const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
      return this.positionsSnapshot.reduce((sum, p) => {
        const qty = num(p.quantity);
        const last = p.last != null ? num(p.last) : num(p.avgCost);
        return sum + qty * last;
      }, 0);
    },
    unrealizedPnl() {
      return this.portfolioStore.unrealizedPnL();
    },
    hintValue(){
      return this.portfolioStore.unrealizedPnLPercentage();
    },
    searchQuery(){
      if (!this.portfolioQuery) return this.portfolios;
      return this.portfolios.filter(p => p.name.toLowerCase().includes(this.portfolioQuery.toLowerCase()))
    },
    formattedValue() {
      if (this.totalValue >= 1e9)
        return (this.totalValue / 1e9).toFixed(2) + "B";
      if (this.totalValue >= 1e6)
        return (this.totalValue / 1e6).toFixed(2) + "M";
      if (this.totalValue >= 1e3)
        return (this.totalValue / 1e3).toFixed(2) + "K";
      return this.totalValue.toFixed(2);
    },
    formattedPortfolioDate() {
      const raw = this.selectedPortfolio?.date
      if (!raw) return '-'
      const d = new Date(raw)
      if (isNaN(d)) return String(raw)
      // Ex.: "Sep 30, 2025"
      return new Intl.DateTimeFormat('en-US', {
        month: 'short', day: '2-digit', year: 'numeric',
      }).format(d)
    },
    formattedLastUpdated() {
      const raw = this.selectedPortfolio?.lastUpdated || Date.now()
      const d = new Date(raw)
      if (isNaN(d)) return String(raw)
      // Ex.: "Sep 30, 2025, 14:07:33"
      return new Intl.DateTimeFormat('en-US', {
        month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).format(d)
    }
  },
  methods: {
    openAlertModal(){
      this.showAlertModal = true;
    },
    onProductChange(value) {
      // O v-btn-toggle com `mandatory` emite undefined ao clicar no ja ativo.
      if (!value || value === this.productType) return;
      this.portfolioStore.setProductType(value);
    },
    compactMoney(value, withSign = true) {
      const n = Number(value) || 0;
      const ccy = this.selectedPortfolio?.currency || 'BRL';
      const sign = withSign ? (n > 0 ? '+' : n < 0 ? '-' : '') : (n < 0 ? '-' : '');
      const abs = Math.abs(n);
      if (abs >= 1e9) return `${sign}${ccy} ${(abs / 1e9).toFixed(2)}B`;
      if (abs >= 1e6) return `${sign}${ccy} ${(abs / 1e6).toFixed(2)}M`;
      if (abs >= 1e3) return `${sign}${ccy} ${(abs / 1e3).toFixed(2)}K`;
      return `${sign}${ccy} ${abs.toFixed(2)}`;
    },
    onSelectPortfolio(p) {
      if (!p) return;
      this.portfolioStore.selectPortfolio(p.id);
    },
    countPositions(p) {
      if (!p) return 0
      const pos = p.positions
      if (Array.isArray(pos)) return pos.length
      if (typeof pos === 'number') return pos
      return 0
    },
    sortBy(key) {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc;
      else {
        this.sortKey = key;
        this.sortAsc = true;
      }
    },
    formatNumber(n) {
      try {
        return new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(n);
      } catch (e) {
        return n;
      }
    },
    openModal(value) {
      if (value === "import") this.showImportModal = true;
      if (value === "new") this.showNewModal = true;
    },
    async handleImport(payload) {
      let rows, name;
      if (Array.isArray(payload)) {
        rows = payload;
        name = payload[0].name;
      } else if (payload && typeof payload === "object") {
        rows = payload.rows;
        name = payload.name;
      }
      if (!rows || !rows.length) return;
      const portfolioName =
        name && String(name).trim()
          ? name
          : this.$t('dVolt.importedDefaultName', { date: new Date().toLocaleDateString() });

        
      this.portfolioStore.addPortfolioWithPositions({
        name: portfolioName,
        currency: rows[0].currency || "USD",
        rows,
      });
    },
    handleDownload (p = this.selectedPortfolio) {
      // Sem portfólio ou sem posições: não faz nada
      if (!p || !Array.isArray(p.positions) || p.positions.length === 0) return;

      // As colunas do CSV sao as do produto do portfolio — nao as do produto
      // selecionado agora na sidebar.
      const isNdfPortfolio = (p.product || 'ETF') === 'NDF';
      const csvTitle = isNdfPortfolio
        ? this.$t('portAr.product.ndfCsvHeaders')
        : this.$t('dVolt.csvHeaders');
      const csvContent = isNdfPortfolio
        ? p.positions.map((row) =>
            `${row.pair}, ${row.side}, ${row.notional}, ${row.strike}, ${row.tradeDate}, ${row.maturity}, ${row.counterparty}, ${row.fixingSource}`
          )
        : p.positions.map((row) =>
            `${row.symbol}, ${row.name}, ${row.quantity}, ${row.avgCost}, ${p.currency}, ${row.aggregationLabel}`
          );

      const finalContent = [csvTitle, ...csvContent].join("\n");
      const blob = new Blob([finalContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${(p.name || "portfolio").replace(/\.[^/.]+$/, "")}.csv`;
      a.click();

      URL.revokeObjectURL(url);
    },
    handleDeletePortfolio(id){
      this.portfolioStore.deletePortfolioWithPosition(id)
    }
  },
};
</script>

<style scoped>

.divider {
  background: var(--border) !important;
}

::v-deep .v-main__wrap {
  height: 100%;
}

.portar-layout {
  display: flex;
  height: 100%;
}

.portar-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* ---------- Seletor de produto (slot nav) ---------- */
.product-switch {
  padding: 12px 16px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.product-switch.is-collapsed {
  padding: 8px 2px;
}
.product-label {
  color: var(--muted);
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.product-toggle {
  width: 100%;
  background: transparent !important;
}
.product-toggle .product-btn {
  flex: 1;
  text-transform: none;
  font-size: 12px;
  letter-spacing: 0;
  color: var(--muted) !important;
  border: 1px solid rgb(38, 38, 38) !important;
}
.product-toggle .product-btn.v-btn--active {
  background: #ffbf00 !important;
  color: #0b0b0b !important;
  font-weight: 700;
}
.product-toggle--icons {
  display: flex;
  flex-direction: column;
}
.product-toggle--icons .product-btn {
  min-width: 32px !important;
  padding: 0 !important;
}

.empty-list {
  color: var(--muted);
  font-size: 12px;
  padding: 16px;
  text-align: center;
}

/* ---------- Sidebar esquerda ---------- */
.sidebar-section {
  padding: 16px;
}
.sidebar-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 12px;
}
.search-input {
  background: #111318 !important;
  border-radius: 8px;
  margin-top: 2rem;
}
.sidebar-actions {
  display: flex;
  gap: 8px;
}
.sidebar-actions > * {
  flex: 1;
}

.btn-primary {
  background: var(--accent) !important;
  color: #000 !important;
  text-transform: none;
  font-weight: 600;
}
.btn-secondary {
  background: #2a2d34 !important;
  color: var(--text) !important;
  text-transform: none;
}

.portfolio-list {
  padding: 10px;
}
.portfolio-item {
  border-bottom: 1px solid rgb(31, 31, 32);
  border-radius: 3px;
  background: none;
  padding: 1rem;
  margin: .4rem 0;
  height:100%;
}
.portfolio-item:hover {
  background: #1b1e24;
}
.portfolio-item.is-active {
  background: #ffbf0092;
  color: #0b0b0b;
}
.portfolio-item.is-active .portfolio-sub {
  color: #0b0b0b;
  opacity: 0.9;
}
.portfolio-name {
  font-size: 12px !important;
  font-weight: 600 !important;
}
.portfolio-sub {
  font-size: 12px;
  color: rgb(149, 149, 149);
}

/* ---------- Header principal ---------- */
.main-header {
  display: flex;
  justify-content: flex-start;
  flex-grow:1;
  align-items: center;
  gap: 1rem;
  height: 72px;
  padding: 0 24px;
  /*border-bottom: 1px solid var(--border);*/
}
.title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text);
}
.subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: var(--muted);
}

/* ---------- Drawer direito (cards) ---------- */
.props-header {
  padding: 24px;
  border-bottom: 1px solid var(--border);
}
.props-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 6px;
}
.props-date {
  color: var(--muted);
  font-size: 13px;
}

.props-cards {
  padding: 20px;
  display: grid;
  grid-auto-rows: minmax(0, auto);
  gap: 10px;
}

.card.box {
  background: none;
  border: 1px solid rgb(38, 38, 38) !important;
  border-radius: 10px;
  padding: 1.1rem;
}
.box-label {
  color: var(--muted);
  font-size: 13px;
  margin: 0.2rem;
  display: inline-block;
}

.box-value {
  color: var(--text);
  font-weight: 700;
  font-size: 1rem;
  margin: 0.3rem;
}
.box-value.danger {
  color: var(--danger);
}
.box-hint {
  color: var(--muted);
  font-size: 12px;
  margin-top: 4px;
}
.strong {
  font-weight: 700;
}

.chip-status-active {
  background: rgba(197, 107, 22, 0.235) !important;
  color: #ffbf00 !important;
  border: 0.06rem solid #ffbf00 !important;
}
.chip-status-desactive {
  background: rgba(216, 11, 11, 0.235) !important;
  color: var(--danger) !important;
  border: 0.06rem solid var(--danger) !important;
}

.positive {
  color: var(--success);
}
.icon-menu{
  padding:0 !important;
  margin:0 !important;
}

.mock-toggle{position:relative;display:inline-flex;align-items:center;font-size:14px;color:var(--text);gap:6px;}
.mock-toggle input{opacity:0;width:0;height:0;}
.mock-toggle .slider{width:34px;height:18px;background-color:var(--accent);border-radius:16px;position:relative;cursor:pointer;}
.mock-toggle .slider::before{content:"";position:absolute;width:14px;height:14px;left:2px;top:2px;background:#fff;border-radius:50%;transition:transform .2s;}
.mock-toggle input:checked + .slider::before{transform:translateX(16px);}

</style>